import React from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
  useToast,
  HStack,
  Flex,
  Link,
  Icon,
} from '@chakra-ui/react';
import { FaCheck } from 'react-icons/fa';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { FcGoogle } from 'react-icons/fc';
import { Header } from '../components/Header';

const MotionBox = motion(Box);
const MotionText = motion(Text);

const PricingCard = ({ title, price, features, isPopular, isProPlan }) => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const highlightedBorderColor = 'blue.400';

  const handleGetStarted = async () => {
    if (title === 'Group Plan') {
      window.location.href = 'mailto:sales@emailed.app?subject=Group%20Plan%20Inquiry';
      return;
    }

    if (!isProPlan) {
      try {
        await signIn();
        navigate('/dashboard');
      } catch (error) {
        toast({
          title: 'Error signing in',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  React.useEffect(() => {
    // Load Stripe script
    if (isProPlan) {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/buy-button.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [isProPlan]);

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      borderWidth="1px"
      borderColor={isPopular ? highlightedBorderColor : borderColor}
      borderRadius="xl"
      overflow="hidden"
      p={6}
      bg={cardBg}
      position="relative"
      boxShadow={isPopular ? 'xl' : 'base'}
      _hover={{ transform: 'translateY(-5px)', transition: 'transform 0.2s' }}
    >
      {isPopular && (
        <Box
          position="absolute"
          top={4}
          right={4}
          bg="blue.400"
          color="white"
          px={3}
          py={1}
          borderRadius="full"
          fontSize="sm"
          fontWeight="bold"
        >
          Popular
        </Box>
      )}
      <VStack spacing={4} align="stretch">
        <Heading size="md">{title}</Heading>
        <Box>
          <Text fontSize="4xl" fontWeight="bold">
            ${price}
          </Text>
          <Text fontSize="sm" color={textColor}>
            per month
          </Text>
        </Box>
        <List spacing={3}>
          {features.map((feature, index) => (
            <ListItem key={index}>
              <ListIcon as={FaCheck} color="green.500" />
              {feature}
            </ListItem>
          ))}
        </List>
        {isProPlan ? (
          <Box mt={4}>
            <stripe-buy-button
              buy-button-id="buy_btn_1QtekqE7b4MZ6iMNNA8UqsCd"
              publishable-key="pk_live_51QsGFBE7b4MZ6iMNI5cqDd4wr1tPQ9fpS2A5mTIQ3thBEvvsMwKdQToXDzNU4bJz9hCqguCp7blEbcC7LtbGLwgx00ey7uAmYu"
            >
            </stripe-buy-button>
          </Box>
        ) : (
          <Button
            colorScheme={isPopular ? 'blue' : 'gray'}
            size="lg"
            w="full"
            mt={4}
            onClick={handleGetStarted}
            leftIcon={title !== 'Group Plan' ? <FcGoogle /> : null}
          >
            {title === 'Group Plan' ? 'Contact Sales' : 'Get Started with Google'}
          </Button>
        )}
      </VStack>
    </MotionBox>
  );
};

const Pricing = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  const plans = [
    {
      title: 'Basic',
      price: '0',
      features: [
        '3 AI-enhanced emails per day',
        'Personal memory storage',
        'Context-aware responses',
        'Basic pattern recognition',
        'Life event tracking'
      ]
    },
    {
      title: 'Pro',
      price: '11',
      features: [
        '30 AI-enhanced emails per day',
        'Advanced memory analysis',
        'Deep pattern recognition',
        'Proactive life insights',
        'Multi-context understanding',
        'Personalized growth tracking'
      ],
      isPopular: true,
      isProPlan: true
    },
    {
      title: 'Group Plan',
      price: 'Custom',
      features: [
        'Unlimited AI-enhanced emails',
        'Advanced memory networks',
        'Cross-context intelligence',
        'Custom AI fine-tuning',
        'Enterprise memory vault',
        'Dedicated AI strategist'
      ]
    }
  ];

  return (
    <Box bg={bgColor} minH="100vh">
      <Header />
      <Box pt="72px">
        <Container maxW="container.xl" py={20}>
          <VStack spacing={8} textAlign="center" mb={12}>
            <Heading size="2xl">Your AI Memory Companion</Heading>
            <Text fontSize="xl" color="gray.500" maxW="800px">
              More than just an email assistant - your personal AI that understands your journey,
              learns from your experiences, and helps you grow through meaningful communication
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
            {plans.map((plan, index) => (
              <PricingCard key={index} {...plan} />
            ))}
          </SimpleGrid>
        </Container>
      </Box>
    </Box>
  );
};

export default Pricing;
